import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Cookie, Shield, BarChart3, Sparkles, Megaphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CookieConsent } from "@/lib/cookieConsent";

interface CookiePreferencesModalProps {
  open: boolean;
  onClose: () => void;
  consent: CookieConsent;
  onSave: (updates: Partial<Omit<CookieConsent, 'essential' | 'timestamp' | 'version'>>) => void;
  onAcceptAll: () => void;
  onRejectNonEssential: () => void;
}

interface CookieCategory {
  id: keyof Omit<CookieConsent, 'timestamp' | 'version'>;
  title: string;
  description: string;
  icon: React.ElementType;
  required?: boolean;
}

const categories: CookieCategory[] = [
  {
    id: 'essential',
    title: 'Essential Cookies',
    description: 'These cookies are necessary for the website to function properly. They enable core functionality such as security, account access, and session management.',
    icon: Shield,
    required: true,
  },
  {
    id: 'analytics',
    title: 'Analytics Cookies',
    description: 'Help us understand how visitors interact with our website by collecting anonymous information. This helps us improve our services.',
    icon: BarChart3,
  },
  {
    id: 'personalization',
    title: 'Personalization Cookies',
    description: 'Allow us to remember your preferences and provide a more personalized experience based on your interests and settings.',
    icon: Sparkles,
  },
  {
    id: 'marketing',
    title: 'Marketing Cookies (Sale/Sharing)',
    description: 'Used to track visitors across websites. Disabling this opts you out of the "sale" or "sharing" of your personal information under CCPA/CPRA.',
    icon: Megaphone,
  },
];

export const CookiePreferencesModal = ({
  open,
  onClose,
  consent,
  onSave,
  onAcceptAll,
  onRejectNonEssential,
}: CookiePreferencesModalProps) => {
  const [preferences, setPreferences] = useState({
    analytics: consent.analytics,
    personalization: consent.personalization,
    marketing: consent.marketing,
  });

  useEffect(() => {
    setPreferences({
      analytics: consent.analytics,
      personalization: consent.personalization,
      marketing: consent.marketing,
    });
  }, [consent]);

  const handleToggle = (id: 'analytics' | 'personalization' | 'marketing') => {
    setPreferences(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleSave = () => {
    onSave(preferences);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh]">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Cookie className="w-5 h-5 text-primary" />
            </div>
            <div>
              <DialogTitle>Cookie Preferences</DialogTitle>
              <DialogDescription>
                Manage your cookie settings
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {categories.map((category) => {
            const Icon = category.icon;
            const isEnabled = category.required 
              ? true 
              : preferences[category.id as keyof typeof preferences];

            return (
              <div 
                key={category.id}
                className="flex items-start gap-4 p-4 rounded-xl border border-border bg-muted/30"
              >
                <div className="w-10 h-10 rounded-lg bg-background flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-4">
                    <h4 className="font-medium text-sm">{category.title}</h4>
                    {category.required ? (
                      <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                        Always On
                      </span>
                    ) : (
                      <Switch
                        checked={isEnabled}
                        onCheckedChange={() => handleToggle(category.id as 'analytics' | 'personalization' | 'marketing')}
                      />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    {category.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Info Text */}
        <div className="text-xs text-muted-foreground space-y-2">
          <p>
            For more information about how we use cookies, please see our{" "}
            <Link to="/cookies" className="text-primary hover:underline" onClick={onClose}>
              Cookie Policy
            </Link>.
          </p>
          <p>
            California residents: Disabling Marketing Cookies opts you out of the sale or sharing of 
            personal information. Learn more on our{" "}
            <Link to="/do-not-sell" className="text-primary hover:underline" onClick={onClose}>
              Do Not Sell page
            </Link>.
          </p>
        </div>

        {/* Actions -- sticky footer so the consent buttons stay reachable
            on short viewports (REK-1939). The category list above can grow
            tall enough to push these buttons below the fold; `sticky
            bottom-0` pins them to the bottom of the DialogScrollArea. */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border bg-background sticky bottom-0">
          <Button
            variant="outline"
            onClick={onRejectNonEssential}
            className="sm:flex-1"
          >
            Reject All
          </Button>
          <Button
            variant="outline"
            onClick={onAcceptAll}
            className="sm:flex-1"
          >
            Accept All
          </Button>
          <Button
            onClick={handleSave}
            className="sm:flex-1"
          >
            Save Preferences
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
